import type { FC } from 'react';
import { useEffect } from 'react';
import { createBrowserRouter, Outlet, RouterProvider, ScrollRestoration, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import FlyingCartAnimation from './components/FlyingCartAnimation';
import Footer from './components/Footer';
import Header from './components/Header';
import LegalNoticePopup from './components/LegalNoticePopup';
import ProtectedRoute from './components/ProtectedRoute';
import ToastContainer from './components/ToastContainer';
import { generateThemeCSS } from './config/theme';
import { AddressProvider } from './contexts/AddressContext';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { FlyingCartProvider } from './contexts/FlyingCartContext';
import { ToastProvider } from './contexts/ToastContext';
import { UserProvider } from './contexts/UserContext';
import { themeManager } from './utils/themeManager';
import { gtagService } from './services/gtagService';
// import ThemeDebugger from './components/ThemeDebugger';
import './App.css';
import About from './pages/About';
import Addresses from './pages/Addresses';
import Cart from './pages/Cart';
import Categories from './pages/Categories';
import Category from './pages/Category';
import Checkout from './pages/Checkout';
import Contact from './pages/Contact';
import Enquiries from './pages/Enquiries';
import EnquiryDetails from './pages/EnquiryDetails';
import EnquirySuccess from './pages/EnquirySuccess';
import Favorites from './pages/Favorites';
import Home from './pages/Home';
import Login from './pages/Login';
import OrderSuccess from './pages/OrderSuccess';
import Privacy from './pages/Privacy';
import ProductDetails from './pages/ProductDetails';
import Products from './pages/Products';
import Profile from './pages/Profile';
import QuickBuy from './pages/QuickBuy';
import Returns from './pages/Returns';
import Safety from './pages/Safety';
import Settings from './pages/Settings';
import Shipping from './pages/Shipping';
import Terms from './pages/Terms';

const Layout: FC = () => {
  const location = useLocation();

  // Initialize Google Analytics on app load
  useEffect(() => {
    // Initialize gtag service that works with the existing HTML implementation
    gtagService.initialize();
  }, []);

  // Track page views on route change
  useEffect(() => {
    gtagService.trackPageView(location.pathname, document.title);
  }, [location]);

  // Initialize theme on app load
  useEffect(() => {
    // Force apply the current theme on app start
    const currentTheme = themeManager.getCurrentTheme();
    console.log('Initializing theme:', currentTheme.name);
    
    // Apply comprehensive theme CSS using the centralized function
    const style = document.createElement('style');
    style.id = 'dynamic-theme';
    style.textContent = generateThemeCSS(currentTheme);
    
    // Remove existing dynamic theme
    const existing = document.getElementById('dynamic-theme');
    if (existing) {
      existing.remove();
    }
    
    document.head.appendChild(style);
  }, []);

  return (
    <ErrorBoundary>
      <ToastProvider>
        <AuthProvider>
          <UserProvider>
            <AddressProvider>
              <CartProvider>
                <FlyingCartProvider>
                  <div className="min-h-screen flex flex-col w-full">
                    <Header />
                    <main className="flex-1 w-full pt-16">
                      <Outlet />
                    </main>
                    <Footer />
                    <ScrollRestoration />
                  </div>
                  <ToastContainer />
                  <FlyingCartAnimation />
                  <LegalNoticePopup />
                  {/* Theme Debug Panel - Remove for production */}
                  {/* {process.env.NODE_ENV === 'development' && <ThemeDebugger />} */}
                </FlyingCartProvider>
              </CartProvider>
            </AddressProvider>
          </UserProvider>
        </AuthProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    children: [
      { path: "/", element: <Home /> },
      { path: "/products", element: <Products /> },
      { path: "/product/:slug", element: <ProductDetails /> },
      { path: "/categories", element: <Categories /> },
      { path: "/category/:slug", element: <Category /> },
      { path: "/cart", element: <Cart /> },
      { path: "/quick-buy", element: <QuickBuy /> },
      { path: "/login", element: <Login /> },
      { 
        path: "/checkout", 
        element: (
          <ProtectedRoute>
            <Checkout />
          </ProtectedRoute>
        )
      },
      { 
        path: "/profile", 
        element: (
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        )
      },
      { 
        path: "/enquiries", 
        element: (
          <ProtectedRoute>
            <Enquiries />
          </ProtectedRoute>
        )
      },
      { 
        path: "/enquiries/:id", 
        element: (
          <ProtectedRoute>
            <EnquiryDetails />
          </ProtectedRoute>
        )
      },
      { 
        path: "/favorites", 
        element: (
          <ProtectedRoute>
            <Favorites />
          </ProtectedRoute>
        )
      },
      { 
        path: "/addresses", 
        element: (
          <ProtectedRoute>
            <Addresses />
          </ProtectedRoute>
        )
      },
      { 
        path: "/settings", 
        element: (
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        )
      },
      { path: "/about", element: <About /> },
      { path: "/contact", element: <Contact /> },
      { path: "/shipping", element: <Shipping /> },
      { path: "/returns", element: <Returns /> },
      { path: "/privacy", element: <Privacy /> },
      { path: "/terms", element: <Terms /> },
      { path: "/safety", element: <Safety /> },
      { 
        path: "/order-success", 
        element: (
          <ProtectedRoute>
            <OrderSuccess />
          </ProtectedRoute>
        )
      },
      { 
        path: "/enquiry-success", 
        element: (
          <ProtectedRoute>
            <EnquirySuccess />
          </ProtectedRoute>
        )
      },
    ],
  },
]);

const App: FC = () => {
  return <RouterProvider router={router} />;
};

export default App;
